import Radio from "common/Radio/Radio";
import {
  Description,
  Title,
} from "features/settings/CancelSubscription/styles.tsx";
import React, { ChangeEvent, useContext } from "react";
import { sharedLib } from "@shared/services/shared.service.ts";
import { CancelSubscriptionContext } from "@shared/features/settings/subscription/CancelSubscription.context.ts";
import { CancellationReason, LanguageNameSpaces } from "@shared/types";
import TextArea from "common/Inputs/TextArea.tsx";
import Text from "common/Typography/Text.tsx";
import styled, { css, useTheme } from "styled-components";
import { MAX_REASON_LENGTH } from "@shared/features/settings/constants.ts";
import { PAGE_SPINNER_MARGIN_FROM_TOP } from "common/constants.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import PageLayout from "features/settings/CancelSubscription/PageLayout.tsx";
import {
  FeedbackMainIcon,
  FeedbackMainIconDark,
} from "features/settings/CancelSubscription/icons.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { hexToRGB } from "@shared/utils/misc";

const CancellationReasons = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const {
    reason,
    setReason,
    otherReasonDescription,
    setOtherReasonDescription,
    isInvalidReasonDescription,
  } = useContext(CancelSubscriptionContext)!;
  const { data, isLoading } =
    sharedLib.sharedApi.settings.useGetCancellationReasonsQuery();

  const getReasonsList = () => {
    return (
      <ReasonsWrapper style={{ flex: "1 0 400px" }}>
        <Radio.Group
          onChange={(e) => {
            setReason(e.target.value);
          }}
          value={reason}
          options={data?.map((item) => ({
            value: item.id,
            label: <Text>{item.label}</Text>,
          }))}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        />
        {reason === CancellationReason.OTHER && (
          <InputWrapper>
            <TextArea
              value={otherReasonDescription}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setOtherReasonDescription(e.target.value)
              }
              placeholder={t("cancel_subscription.tell_us_more")}
            />
            <Text
              $size="S"
              style={{
                textAlign: "right",
                marginTop: 4,
                color: isInvalidReasonDescription
                  ? theme.colors.red
                  : theme.colors.midDark,
              }}
            >
              {otherReasonDescription?.length}/{MAX_REASON_LENGTH}
            </Text>
          </InputWrapper>
        )}
      </ReasonsWrapper>
    );
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      spinnerSize="large"
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      hideContentWhileLoading={true}
    >
      <PageLayout
        topContainer={
          <>
            <Title>{t("cancel_subscription.every_goodbye_matters")}</Title>
            <Description>
              {t("cancel_subscription.your_feedback_helps")}
            </Description>
          </>
        }
        mainContent={getReasonsList()}
        Icon={theme.isDarkMode ? FeedbackMainIconDark : FeedbackMainIcon}
      />
    </LoadingSpinnerOverlay>
  );
};

export default CancellationReasons;

const InputWrapper = styled.div`
  margin-left: 20px;
  margin-top: 8px;
  max-width: 440px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin: 12px 0 0;
      max-width: none;
    `}
`;
/** New look: each reason is a full-width selectable row. */
const ReasonsWrapper = styled.div`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Rows on hairlines (not boxes in the panel); the chosen row is
         marked by its radio and a light selection tint. */
      .ant-radio-group {
        gap: 0 !important;
        border-top: 1px solid ${getNewLookTokens(p.theme).hairline};
      }
      .ant-radio-wrapper {
        margin: 0;
        min-height: 48px;
        align-items: center;
        padding: 0 12px;
        border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
        ${newLookType.body};
        color: ${getNewLookTokens(p.theme).text};
        transition: background-color 120ms;
        &:hover {
          background: ${getNewLookTokens(p.theme).rowHover};
        }
      }
      .ant-radio-wrapper-checked,
      .ant-radio-wrapper-checked:hover {
        background: ${hexToRGB(getNewLookTokens(p.theme).accent, 0.06)};
      }
    `}
`;
